'use client';

import dynamic from 'next/dynamic';
import { redirect, useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useRef } from 'react';
import { useRecoilState } from 'recoil';

import {
  comfortOptions,
  driveTypeOptions,
  fuelTypeOptions,
  otherOptions,
  safetyAndTechOptions,
  transmissionTypeOptions,
} from '@/constants/filters/filters.constants';
import { STATUS_SPECIALS } from '@/constants/specials/specials.constants';
import { useVehicleInfo } from '@/hooks/details-car/vehicle-info.hook';
import { useBodyTypesFilter } from '@/hooks/filters/body-types-filter.hook';
import { useBrandsRangesFilter } from '@/hooks/filters/brands-ranges-filter.hook';
import { useColorsFilter } from '@/hooks/filters/colors-filter.hook';
import { useDoorsFilter } from '@/hooks/filters/doors-filter.hook';
import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';
import { useRoutesConfig } from '@/hooks/routes-config';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';
import { useProvincesDealershipsFilter } from '@/hooks/used-cars/provinces-dealerships-filter.hook';
import { useVehicles } from '@/hooks/used-cars/vehicles.hook';
import { getSearchResultPath } from '@/services/link.service';
import { stringifyQuery } from '@/services/QueryStringService';
import {
  filterForVehicles,
  getAllAvailableSpecials,
} from '@/services/SpecialsService.service';
import {
  isSearchOpenState,
  mustUseNewFiltersState,
} from '@/states/used-cars/filter-query.state';
import {
  UsedCarsPageProps,
  VehicleType,
} from '@/types/used-cars/used-cars.types';
import { getCarType } from '@/utils/used-cars';

import { BlockWrapper } from './block-wrapper/block-wrapper';
import { Breadcrumbs } from './breadcrumbs/breadcrumbs';
import { MainSearch } from './filters/main-search/main-search';
import { PageContent } from './page-content/page-content';
import { SearchFiltersWrapper } from './search/search-filters-wrapper/search-filters-wrapper';
import { PageTitle } from './typography/page-title/page-title';
import styles from './UsedCars.module.scss';

const SearchFilters = dynamic(() =>
  import('./search/search-filters/search-filters').then(
    (filter) => filter.SearchFilters,
  ),
);

const UsedCars: React.FC<UsedCarsPageProps> = ({
  carType,
  brands,
  defaultCarPrice,
  bodyTypes,
  colourTypes,
  doorsTypes,
  defaultCarYears,
  defaultCarMileages,
  provincesWithDealerships,
  defaultConsumption,
  vehicles,
  specialsVehicles,
  vehiclesMeta,
  fuelTypeCount,
  transmissionCount,
  driveTrainCount,
  featuresCount,
  activeDealershipId,
  openingParagraph,
}) => {
  const { setColorFilters, resetSelectedColorFilter } = useColorsFilter();
  const { setDoorsFilter, resetSelectedDoorsFilter } = useDoorsFilter();
  const { setBodyTypesFilter, resetSelectedBodyTypes } = useBodyTypesFilter();
  const { setDefaultPriceFilter, resetPriceFilter } = usePriceFilter();
  const { setYearsFilterDefault, resetYearsFilter } = useYearsFilterHook();
  const { setBrandsRangesFilter, resetBrandsRanges } = useBrandsRangesFilter();
  const { setFeaturesFilter, resetSelectedFeatures, setFeaturesCount } =
    useFeaturesFilter();
  const { setProvincesDealershipsFilter, resetProvincesDealerships } =
    useProvincesDealershipsFilter();
  const {
    setTransmissionTypeFilter,
    resetSelectedTransmissionTypes,
    setTransmissionCount,
  } = useTransmissionTypeFilter();
  const { setFuelTypeFilter, resetSelectedFuelTypes, setFuelTypeCount } =
    useFuelTypeFilter();
  const {
    setDrivetrainTypesFilter,
    resetSelectedDrivetrainTypes,
    setDriveTrainCount,
  } = useDrivetrainFilter();
  const { resetFinanceState, setSelectedFinanceFilter } = useFinanceFilter();
  const { setMileageFilterDefault, resetMileageFilter } = useMileageFilter();
  const { setConsumptionFilterDefault, resetConsumptionFilter } =
    useFuelConsumptionFilter();
  const { setSimilarVehicles } = useVehicleInfo();
  const { setVehicles, setVehiclesMeta } = useVehicles();
  const {
    hasFiltersQuery,
    filtersQuery,
    activeCarType,
    isCleared,
    setActiveCarType,
    setIsCleared,
  } = useFiltersQuery();
  const routes = useRoutesConfig();
  const router = useRouter();
  const [isSearchOpen] = useRecoilState(isSearchOpenState);
  const isFirstRender = useRef(true);

  useEffect(() => {
    vehicles && setVehicles(vehicles);
    vehiclesMeta && setVehiclesMeta(vehiclesMeta);
    resetProvincesDealerships(null);
    setSimilarVehicles(specialsVehicles);
    setBrandsRangesFilter(brands);
    setDefaultPriceFilter(defaultCarPrice);
    setColorFilters(colourTypes);
    setDoorsFilter(doorsTypes);
    setFeaturesFilter((features) => ({
      ...features,
      comfort: comfortOptions,
      safetyAndTech: safetyAndTechOptions,
      other: otherOptions,
    }));
    setFeaturesCount(featuresCount);
    setYearsFilterDefault(defaultCarYears);
    setMileageFilterDefault(defaultCarMileages);
    setConsumptionFilterDefault(defaultConsumption);
    setProvincesDealershipsFilter(provincesWithDealerships);
    setTransmissionTypeFilter(transmissionTypeOptions);
    setTransmissionCount(transmissionCount);
    setFuelTypeFilter(fuelTypeOptions);
    setFuelTypeCount(fuelTypeCount);
    setDrivetrainTypesFilter(driveTypeOptions);
    setDriveTrainCount(driveTrainCount);
    resetFinanceState();
    setActiveCarType(carType);
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    setBodyTypesFilter(bodyTypes);
  }, [bodyTypes]);

  useEffect(() => {
    resetPriceFilter();
    setSelectedFinanceFilter('');
    resetYearsFilter();
    resetMileageFilter();
    resetConsumptionFilter();
    resetBrandsRanges(null);
    resetSelectedBodyTypes(null);
    resetSelectedColorFilter(null);
    resetSelectedDoorsFilter(null);
    resetSelectedTransmissionTypes(null);
    resetSelectedFuelTypes(null);
    resetSelectedDrivetrainTypes(null);
    resetSelectedFeatures(null);
    setIsCleared(false);
  }, []);

  useEffect(() => {
   
    //TODO: remove console
    // !filtersQuery.dealership_id is causing issue for setting the serch query
    if (!isFirstRender.current) {
      if (
        (hasFiltersQuery || isCleared) &&
        filtersQuery &&
        !filtersQuery.dealership_id
      ) {
        const query = stringifyQuery(filtersQuery);

        router.push(`${getSearchResultPath(activeCarType, routes)}?${query}`);
      } else {
        setIsCleared(false);
      }
    } else {
      isFirstRender.current = false;
    }
  }, [filtersQuery]);

  const isUsingNewFilters = useRecoilState(mustUseNewFiltersState);

  return (
    <React.Fragment>
      <Suspense fallback={null}>
        <InitialQueryInfo carType={carType} />
      </Suspense>
      <BlockWrapper>
        <Breadcrumbs
          items={[
            {
              name:
                (activeCarType ?? carType) === VehicleType.Demo
                  ? routes.demo.name
                  : routes.usedCars.name,
              path:
                (activeCarType ?? carType) === VehicleType.Demo
                  ? routes.demo.path
                  : routes.usedCars.path,
            },
          ]}
          compact={false}
        />
      </BlockWrapper>
      <BlockWrapper>
        <PageTitle>
          {(activeCarType ?? carType) === VehicleType.Demo
            ? 'Demo Used Cars'
            : 'Used Cars'}
        </PageTitle>
      </BlockWrapper>
      {isUsingNewFilters ? (
        <SearchFiltersWrapper>
          <MainSearch />
        </SearchFiltersWrapper>
      ) : (
        <SearchFilters
          pageType={
            (activeCarType ?? carType) === VehicleType.Demo ? 'demo' : 'used'
          }
        />
      )}
      <div className={styles.bodyWrapper}>
        {isSearchOpen && <div className={styles.bodyWrapperBlur}></div>}
        <PageContent
          brands={brands}
          provinces={provincesWithDealerships}
          bodyTypes={bodyTypes}
          carType={carType}
          headingContentText={
            openingParagraph && openingParagraph?.length !== 0
              ? openingParagraph.at(0)?.title || ''
              : (activeCarType ?? carType) === VehicleType.Demo
              ? 'Search all Demo Cars'
              : 'Search all Used Cars'
          }
          headingContentTextDemo={
            openingParagraph && openingParagraph?.length !== 0
              ? openingParagraph.at(0)?.description || ''
              : ''
          }
          activeDealershipId={activeDealershipId as any}
        />
      </div>
    </React.Fragment>
  );
};

function InitialQueryInfo({ carType }: { carType: VehicleType }) {
  const { setSimilarVehicles } = useVehicleInfo();
  const { setActiveCarType } = useFiltersQuery();

  const searchParams = useSearchParams();

  useEffect(() => {
    const makeParam = searchParams?.get('make');

    if (carType === VehicleType.Demo && makeParam) {
      redirect('/used-cars/demo');
    }

    if (carType === VehicleType.Used && makeParam) {
      const make = Array.isArray(makeParam)
        ? makeParam.map((make) => make.toLowerCase())
        : makeParam.toLowerCase();

      redirect(`/used-cars/${make}`);
    }

    const carTypeParam = searchParams?.get(carType);

    if (carTypeParam) {
      const activeType = getCarType(carTypeParam);

      setActiveCarType(activeType);

      getNewSpecialsByCarType(carTypeParam);
    }
  }, [searchParams]);

  const getNewSpecialsByCarType = async (carType: string) => {
    let type;

    switch (carType) {
      case 'used':
        type = VehicleType.Used;
        break;
      case 'demo':
        type = VehicleType.Demo;
        break;
      default:
        break;
    }

    const specials = await getAllAvailableSpecials({
      ...STATUS_SPECIALS,
      vehicle_type: { _eq: type || '0' },
    });
    let specialsVehicles;

    if (specials.length !== 0) {
      specialsVehicles = await filterForVehicles(specials);
    } else {
      specialsVehicles = [];
    }
    setSimilarVehicles(specialsVehicles);
  };

  return null;
}

export default UsedCars;
